import isTouchDevice from 'is-touch-device';
import { useEffect, useCallback, useMemo, useState } from 'react';

type UseIsTouchDevice = (() => boolean) & {
  confirmedTouch?: boolean;
  init: () => void;
};

const useIsTouchDevice: UseIsTouchDevice = () => {
  const isTouchFromDevice = useMemo(() => isTouchDevice(), []);
  const [checked, setChecked] = useState(typeof useIsTouchDevice.confirmedTouch !== 'undefined');

  // This event is used to force the hook to refresh
  const handleMouseMouseOrTouchStart = useCallback(() => {
    setChecked(true);
  }, []);

  useEffect(() => {
    // We check both in the case where confirmedTouch was set between the useState and the useEffect
    if (!checked && typeof useIsTouchDevice.confirmedTouch === 'undefined') {
      window.addEventListener('touchstart', handleMouseMouseOrTouchStart);
      window.addEventListener('mousemove', handleMouseMouseOrTouchStart);
    }

    return () => {
      window.removeEventListener('touchstart', handleMouseMouseOrTouchStart);
      window.removeEventListener('mousemove', handleMouseMouseOrTouchStart);
    };
  }, [checked, handleMouseMouseOrTouchStart]);

  return (typeof useIsTouchDevice.confirmedTouch !== 'undefined') ?
    useIsTouchDevice.confirmedTouch :
    isTouchFromDevice;
};

useIsTouchDevice.init = () => {
  useIsTouchDevice.confirmedTouch = undefined;
  // We want to start watching as soon as possible, so we work outside the hook
  const handleTouchstart = () => {
    useIsTouchDevice.confirmedTouch = true;
    removeListeners();
  };

  const handleMouseMouse = () => {
    useIsTouchDevice.confirmedTouch = false;
    removeListeners();
  };

  const removeListeners = () => {
    window.removeEventListener('touchstart', handleTouchstart);
    window.removeEventListener('mousemove', handleMouseMouse);
  };

  window.addEventListener('touchstart', handleTouchstart);
  window.addEventListener('mousemove', handleMouseMouse);
};

export default useIsTouchDevice;
